import { isEmpty } from "lodash"
import React, { useEffect, useState } from "react"
import { Spinner, Table } from "react-bootstrap"
import ReactFlagsSelect from "react-flags-select"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import EditIcon from "../../../assets/edit.svg"
import TrashIcon from "../../../assets/trash.svg"
import { SERVICE_TYPE } from "../../../constants/constants"
import { RootState } from "../../../store"
import { loadValueAddedServices } from "../../../store/pricing.store"
import UpdateOrCreateCustomerAddedValueService from "./UpdateOrCreateCustomerAddedValueService"

const ValueAddedServicesTab = ({
    customerValueAddedServices,
    warehouses,
    customer,
    setUpdateModalWarehouse,
    variationData,
    setVariationData,
    loading,
    requestsLoader,
    selectedWarehouse,
    setSelectedWarehouse,
    handleSubmit,
    showConfirmModal,
    setShowConfirmModal
}: {
    customerValueAddedServices: Record<string, any>[]
    warehouses: Record<string, any>[]
    customer: Record<string, any>
    setUpdateModalWarehouse: Function
    variationData: Record<string, any>
    setVariationData: Function
    loading: boolean
    requestsLoader: boolean
    selectedWarehouse: Record<string, any>
    setSelectedWarehouse: Function
    handleSubmit: Function
    showConfirmModal: string
    setShowConfirmModal: Function
}) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { valueAddedServices, loading: servicesLoading } = useAppSelector((state: RootState) => state.pricing)
    const { selectedWarehouse: userSelectedWarehouse } = useAppSelector((state: RootState) => state.warehouses)
    const [newRate, setNewRate] = useState(0)
    const [rateType, setRateType] = useState("")
    const [variationUpdatedData, setVariationUpdatedData] = useState({} as Record<string, any>)

    const createWarehousesLabels = () => {
        const customLabels = {} as Record<string, any>
        warehouses?.forEach((w) => {
            return (customLabels[w?.country] = {
                primary: w?.country
            })
        })
        return customLabels
    }

    useEffect(() => {
        if (selectedWarehouse?.id) {
            dispatch(loadValueAddedServices({ warehouse: selectedWarehouse?.id?.toString() }))
        } else {
            if (userSelectedWarehouse) {
                dispatch(loadValueAddedServices({ warehouse: userSelectedWarehouse?.toString() }))
            }
        }
    }, [selectedWarehouse?.id, userSelectedWarehouse])

    return loading || requestsLoader || servicesLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" />
        </div>
    ) : (
        <>
            <div className="table-responsive">
                <Table>
                    <thead className="custom-thead">
                        <tr className="border rounded">
                            <th className="col-3">Services</th>
                            <th className="col-2">Warehouse</th>
                            <th className="col-2">Default</th>
                            <th className="col-3">New Rates</th>
                            <th className="col-1">Action</th>
                        </tr>
                    </thead>
                    <tbody className="text-start custom-tbody">
                        {customerValueAddedServices?.map((vas) =>
                            !isEmpty(variationUpdatedData) &&
                            variationUpdatedData?.id === vas?.id &&
                            showConfirmModal === "" ? (
                                <UpdateOrCreateCustomerAddedValueService
                                    variationData={variationUpdatedData}
                                    setVariationData={setVariationUpdatedData}
                                    selectedWarehouse={selectedWarehouse}
                                    valueAddedServices={valueAddedServices}
                                    customer={customer}
                                    newRate={newRate}
                                    setNewRate={setNewRate}
                                    rateType={rateType}
                                    setRateType={setRateType}
                                    handleSubmit={handleSubmit}
                                    setSelectedWarehouse={setSelectedWarehouse}
                                    setUpdateModalWarehouse={setUpdateModalWarehouse}
                                    setShowConfirmModal={setShowConfirmModal}
                                    customerValueAddedServices={customerValueAddedServices}
                                />
                            ) : (
                                <tr className="rounded justify-content-around" key={`${vas?.id}-shipping-rate`}>
                                    <td className="col-3">
                                        <span>{vas?.service}</span>
                                    </td>
                                    <td className="col-2 d-flex align-items-start">
                                        <Flag className="w-25" code={vas?.Warehouse?.country} />
                                        &nbsp;&nbsp;<span>{vas?.Warehouse?.country}</span>
                                    </td>
                                    <td className="col-2 d-flex">
                                        <span>
                                            {valueAddedServices?.find((v) => v?.service === vas?.service)
                                                ?.service_value || 0}
                                        </span>
                                        <span>
                                            {valueAddedServices?.find((v) => v?.service === vas?.service)
                                                ?.service_type === SERVICE_TYPE["0"]
                                                ? "$"
                                                : "%"}
                                        </span>
                                    </td>
                                    <td className="col-3 d-flex">
                                        <div>
                                            <span>{vas?.service_value || 0}</span>
                                            <span>{vas?.service_type === SERVICE_TYPE["0"] ? "$" : "%"}</span>
                                        </div>
                                        {vas?.is_service_charge ? (
                                            <div className="mx-1 text-muted">
                                                <small>
                                                    &nbsp;-&nbsp;{t("Min val ")} : {vas?.min_value || 0}$
                                                </small>
                                            </div>
                                        ) : null}
                                    </td>
                                    <td className="col-1 d-flex justify-content-between align-items-start">
                                        <img
                                            src={EditIcon}
                                            onClick={() => {
                                                setNewRate(vas?.slabs)
                                                setRateType(
                                                    vas?.slabs?.map((s: Record<string, any>) => {
                                                        return {
                                                            rate_slab_id: s?.id,
                                                            rate_slab_discount: s?.discount
                                                        }
                                                    })
                                                )
                                                setSelectedWarehouse(vas?.warehouse)
                                                setUpdateModalWarehouse(vas?.warehouse_id)
                                                setVariationUpdatedData(vas)
                                            }}
                                            className="tr-pointer pb-1 w-50"
                                        />
                                        <img
                                            src={TrashIcon}
                                            onClick={() => {
                                                setVariationData({ ...vas })
                                                setShowConfirmModal("deleteVasDiscount")
                                            }}
                                            className="tr-pointer pb-1 w-50 mx-1"
                                        />
                                    </td>
                                </tr>
                            )
                        )}
                        {!isEmpty(variationData) && showConfirmModal === "" && (
                            <UpdateOrCreateCustomerAddedValueService
                                variationData={variationData}
                                setVariationData={setVariationData}
                                selectedWarehouse={selectedWarehouse}
                                valueAddedServices={valueAddedServices}
                                customer={customer}
                                newRate={newRate}
                                setNewRate={setNewRate}
                                rateType={rateType}
                                setRateType={setRateType}
                                handleSubmit={handleSubmit}
                                setSelectedWarehouse={setSelectedWarehouse}
                                setUpdateModalWarehouse={setUpdateModalWarehouse}
                                setShowConfirmModal={setShowConfirmModal}
                                customerValueAddedServices={customerValueAddedServices}
                            />
                        )}
                    </tbody>
                </Table>
            </div>
            <div className="d-flex justify-content-end">
                <ReactFlagsSelect
                    className="add-variant"
                    countries={warehouses
                        ?.filter((warehouse) => warehouse.active)
                        ?.map((warehouse) => warehouse.country)}
                    selected={selectedWarehouse?.country || ""}
                    showSelectedLabel
                    placeholder={"+ Add Variant"}
                    customLabels={createWarehousesLabels()}
                    onSelect={(code: string) => {
                        const serviceWarehouse = warehouses?.find((w) => w?.country === code)
                        setVariationData({
                            ...variationData,
                            warehouse_id: serviceWarehouse?.id
                        })
                        setVariationUpdatedData({})
                        setSelectedWarehouse(serviceWarehouse || {})
                        setUpdateModalWarehouse(serviceWarehouse?.id)
                    }}
                />
            </div>
        </>
    )
}

export default ValueAddedServicesTab
